import { Button, Table } from '@heroui/react'

import type { PosRegister } from '../types'
import { RegisterStatus } from '../types'
import {
  branchLabel,
  catalogueStyles,
  chipStyle,
  registerHealthStyles,
  relativeTime,
} from './pos-format'
import { StatusChip } from './status-chip'

interface RegistersTableProps {
  registers: PosRegister[]
  /** Omitted when the signed-in user may read registers but not retire one. */
  onRetire?: (register: PosRegister) => void
  retiringId?: string | null
}

/**
 * Which tills exist, and whether they are answering.
 *
 * There is no add button and no edit. A register is claimed by the DEVICE on
 * first sign-in, because a till that had to be provisioned from a laptop could
 * not replace a dead tablet mid-service. Retiring one is the only write, and it
 * is a statement about a device that has gone.
 */
export function RegistersTable({ registers, onRetire, retiringId }: RegistersTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Registers">
          <Table.Header>
            <Table.Column id="code" isRowHeader>
              Register
            </Table.Column>
            <Table.Column id="branch">Branch</Table.Column>
            <Table.Column id="device">Device</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="catalogue">Catalogue</Table.Column>
            <Table.Column id="lastSeen">Last seen</Table.Column>
            {onRetire && <Table.Column id="actions">{/* Retire */}</Table.Column>}
          </Table.Header>

          <Table.Body>
            {registers.map((register) => (
              <Table.Row key={register.id} id={register.id}>
                <Table.Cell>
                  <span className="font-medium">{register.code}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-accent">{branchLabel(register.branch)}</span>
                </Table.Cell>

                <Table.Cell>
                  {/* Model and station read as one phrase — "iPad · Front
                      counter" is how somebody describes the till they mean. */}
                  {register.deviceModel || register.stationName ? (
                    <span className="text-accent">
                      {[register.deviceModel, register.stationName].filter(Boolean).join(' · ')}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <StatusChip style={chipStyle(registerHealthStyles, register.health)} />
                </Table.Cell>

                <Table.Cell>
                  <StatusChip style={chipStyle(catalogueStyles, register.catalogue)} />
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted tabular-nums">
                    {relativeTime(register.lastSeenAt)}
                  </span>
                </Table.Cell>

                {onRetire && (
                  <Table.Cell>
                    {register.status === RegisterStatus.Active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="border-border h-8 rounded-full border px-3 text-xs"
                        isDisabled={retiringId === register.id}
                        onPress={() => onRetire(register)}
                      >
                        Retire
                      </Button>
                    ) : (
                      <span className="text-muted text-xs">Retired</span>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
